import React, { useState, useEffect } from 'react';
import { FileText, Upload, CheckCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

const TIPOS_DOCUMENTO = {
  cliente: [
    { tipo: 'cartao_cnpj', label: 'Cartão CNPJ', obrigatorio: true },
    { tipo: 'contrato_social', label: 'Contrato Social', obrigatorio: true },
    { tipo: 'documento_socio', label: 'Documento do Sócio', obrigatorio: true },
  ],
  entregador: [
    { tipo: 'cartao_cnpj', label: 'Cartão CNPJ', obrigatorio: true },
    { tipo: 'contrato_social', label: 'Contrato Social', obrigatorio: true },
    { tipo: 'documento_socio', label: 'Documento do Sócio', obrigatorio: true },
    { tipo: 'cnh', label: 'CNH', obrigatorio: true },
  ]
};

const Documentos = () => {
  const { empresa, atualizarEmpresa } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  const [urlArquivo, setUrlArquivo] = useState('');
  const [nomeArquivo, setNomeArquivo] = useState('');

  const tiposDocumento = TIPOS_DOCUMENTO[empresa?.tipo_empresa] || TIPOS_DOCUMENTO.cliente;

  useEffect(() => {
    carregarDocumentos();
    atualizarDadosEmpresa();
  }, []);

  const atualizarDadosEmpresa = async () => {
    try {
      const response = await api.get(`/empresas/${empresa.id}`);
      atualizarEmpresa(response.data);
    } catch (error) {
      console.error('Erro ao atualizar empresa:', error);
    }
  };

  const carregarDocumentos = async () => {
    try {
      const response = await api.get(`/documentos/empresa/${empresa.id}`);
      setDocumentos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!tipoSelecionado || !urlArquivo || !nomeArquivo) return;

    setLoading(true);
    try {
      await api.post('/documentos/upload', {
        empresa_id: empresa.id,
        tipo_documento: tipoSelecionado,
        url_arquivo: urlArquivo,
        nome_arquivo: nomeArquivo
      });

      toast.success('Documento enviado com sucesso!');
      setOpenUpload(false);
      setUrlArquivo('');
      setNomeArquivo('');
      setTipoSelecionado(null);
      carregarDocumentos();
      atualizarDadosEmpresa();
    } catch (error) {
      toast.error('Erro ao enviar documento');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aprovado':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejeitado':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'aprovado':
        return 'Aprovado';
      case 'rejeitado':
        return 'Rejeitado';
      default:
        return 'Pendente';
    }
  };

  const getDocumentoEnviado = (tipo) => {
    return documentos.find(d => d.tipo_documento === tipo);
  };

  const todosEnviados = tiposDocumento
    .filter(t => t.obrigatorio)
    .every(t => getDocumentoEnviado(t.tipo));

  const todosAprovados = tiposDocumento
    .filter(t => t.obrigatorio)
    .every(t => {
      const doc = getDocumentoEnviado(t.tipo);
      return doc && doc.status === 'aprovado';
    });

  return (
    <DashboardLayout title="Documentos">
      {/* Status Geral */}
      <div className="mb-6">
        {empresa.status_validacao === 'aprovado' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">Cadastro Aprovado</p>
              <p className="text-sm text-green-700">Sua empresa está habilitada para operar na plataforma.</p>
            </div>
          </div>
        ) : empresa.status_validacao === 'rejeitado' ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-600" />
            <div>
              <p className="font-semibold text-red-900">Cadastro Rejeitado</p>
              <p className="text-sm text-red-700">Verifique os documentos rejeitados e reenvie.</p>
            </div>
          </div>
        ) : empresa.reaberto_em ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3" data-testid="banner-cadastro-reaberto">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
            <div>
              <p className="font-semibold text-orange-900">Cadastro reaberto pelo Admin</p>
              <p className="text-sm text-orange-700">
                Reenvie os documentos abaixo para nova análise. Data da reabertura:{' '}
                {new Date(empresa.reaberto_em).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900">Validação Pendente</p>
              <p className="text-sm text-amber-700">
                {!todosEnviados 
                  ? 'Envie todos os documentos obrigatórios para análise.'
                  : 'Seus documentos estão em análise. Aguarde a aprovação.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Lista de Documentos */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Documentos Obrigatórios
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Envie todos os documentos para liberar seu cadastro na plataforma.
          </p>
        </div>

        <div className="divide-y divide-slate-100">
          {tiposDocumento.map((tipo) => {
            const docEnviado = getDocumentoEnviado(tipo.tipo);
            
            return (
              <div key={tipo.tipo} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${docEnviado ? (docEnviado.status === 'aprovado' ? 'bg-green-100' : docEnviado.status === 'rejeitado' ? 'bg-red-100' : 'bg-amber-100') : 'bg-slate-100'}`}>
                    <FileText className={`w-5 h-5 ${docEnviado ? (docEnviado.status === 'aprovado' ? 'text-green-600' : docEnviado.status === 'rejeitado' ? 'text-red-600' : 'text-amber-600') : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {tipo.label}
                      {tipo.obrigatorio && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    {docEnviado && (
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusIcon(docEnviado.status)}
                        <span className="text-sm text-slate-600">{getStatusLabel(docEnviado.status)}</span>
                        {docEnviado.motivo_rejeicao && (
                          <span className="text-sm text-red-600">- {docEnviado.motivo_rejeicao}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  {(() => {
                    const reaberto = empresa?.status_validacao === 'cadastro_iniciado' || empresa?.status_validacao === 'rejeitado';
                    const ehReenvio = docEnviado && (docEnviado.status === 'rejeitado' || reaberto);
                    const naoEnviadoEAdminReabriu = !docEnviado && empresa?.reaberto_em;
                    
                    if (!docEnviado && !naoEnviadoEAdminReabriu) {
                      return (
                        <Button
                          onClick={() => { setTipoSelecionado(tipo.tipo); setOpenUpload(true); }}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`btn-enviar-${tipo.tipo}`}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Enviar
                        </Button>
                      );
                    }
                    if (ehReenvio || naoEnviadoEAdminReabriu) {
                      return (
                        <div className="flex flex-col items-end gap-1">
                          <Button
                            onClick={() => { setTipoSelecionado(tipo.tipo); setOpenUpload(true); }}
                            variant="outline"
                            className="border-amber-600 text-amber-600 hover:bg-amber-50"
                            data-testid={`btn-reenviar-${tipo.tipo}`}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Reenviar
                          </Button>
                          {docEnviado && (
                            <span className="text-[10px] text-slate-500">
                              Último envio: {new Date(docEnviado.enviado_em).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      );
                    }
                    return (
                      <span className="text-sm text-slate-500">
                        Enviado em {new Date(docEnviado.enviado_em).toLocaleDateString('pt-BR')}
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Informações */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Informações Importantes</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>Os documentos devem estar legíveis e atualizados.</li>
          <li>O CNPJ deve estar ativo na Receita Federal.</li>
          <li>A análise dos documentos pode levar até 48 horas úteis.</li>
          <li>Você será notificado por e-mail sobre o resultado da análise.</li>
        </ul>
      </div>

      {/* Modal Upload */}
      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Documento</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Tipo de Documento</label>
              <p className="text-slate-900 font-medium capitalize">
                {tiposDocumento.find(t => t.tipo === tipoSelecionado)?.label}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Nome do Arquivo *</label>
              <Input
                value={nomeArquivo}
                onChange={(e) => setNomeArquivo(e.target.value)}
                placeholder="Ex: cnpj_empresa.pdf"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">URL do Arquivo *</label>
              <Input
                value={urlArquivo}
                onChange={(e) => setUrlArquivo(e.target.value)}
                placeholder="https://exemplo.com/documento.pdf"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Faça upload do arquivo em um serviço de armazenamento e cole a URL aqui.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Nota:</strong> No ambiente de produção, será possível fazer upload direto do arquivo.
                Por enquanto, utilize uma URL de um arquivo já hospedado.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setOpenUpload(false);
                  setUrlArquivo('');
                  setNomeArquivo('');
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading || !urlArquivo || !nomeArquivo}
              >
                {loading ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Documentos;
